import React from 'react';
import { exhaustiveGuard } from '@soundxyz/utils';
import {
  type PresaleDetails,
  type PublicSaleDetails,
} from '@components/Song/MintComponents/MintComponent/Edition/useMintState';
import { FormatStrictCoundown } from '@components/basics/FormatStrictCountdown';
import { useAuth } from '@context/auth/base';
import { useIsSaleInMoreThanADay } from '../useIsSaleInMoreThanADay';
import { useMintComponentHeading } from '../useMintComponentHeading';

//TODO: Add tests : https://linear.app/sound/issue/ENG-7510/add-test-to-panel-subinfo-helpers
export const MintComponentPanelSubInfo = ({
  saleDetails,
  selectedPanel,
  hasPresale,
  hasPresaleAccess,
}: {
  saleDetails: NonNullable<PublicSaleDetails | PresaleDetails>;
  selectedPanel: 'PUBLIC' | 'PRESALE';
  hasPresale: boolean;
  hasPresaleAccess: boolean;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();

  const isSaleInMoreThanADay = useIsSaleInMoreThanADay({ timeStamp: saleDetails.startTime });

  const heading = useEditionMintComponentHeading({
    isAuthenticatedWithAPI,
    hasPresale,
    isSaleInMoreThanADay,
    selectedSale: saleDetails,
    selectedPanel,
    hasPresaleAccess,
  });

  if (!heading) return null;

  return (
    <div className="flex flex-col items-center gap-0.5 pb-1 text-center font-base text-base-s text-white">
      <div className="inline-flex font-semibold">
        {heading.message}&nbsp;
        {heading.showTimer && (
          <FormatStrictCoundown timeStamp={saleDetails.startTime} expandedFormat hideUnit />
        )}
      </div>
    </div>
  );
};

export const useEditionMintComponentHeading = ({
  isAuthenticatedWithAPI,
  hasPresale,
  isSaleInMoreThanADay,
  selectedSale,
  selectedPanel,
  hasPresaleAccess,
}: {
  isAuthenticatedWithAPI: boolean;
  isSaleInMoreThanADay: boolean;
  selectedSale: NonNullable<PublicSaleDetails | PresaleDetails>;
  selectedPanel: 'PUBLIC' | 'PRESALE';
  hasPresale: boolean;
  hasPresaleAccess: boolean;
}) => {
  const isPresale = selectedPanel === 'PRESALE';
  const hasOngoingSale = selectedSale.saleOngoing;
  const saleEnded = selectedSale.saleEnded;
  const isSoldOut = selectedSale.isSoldOut;

  const canMintSale = (() => {
    switch (selectedPanel) {
      case 'PRESALE':
        return 'merkleRoot' in selectedSale ? selectedSale.canMintPresale : false;
      case 'PUBLIC':
        return 'canMintPublicSale' in selectedSale ? selectedSale.canMintPublicSale : false;
      default:
        exhaustiveGuard(selectedPanel);
    }
  })();

  return useMintComponentHeading({
    isAuthenticatedWithAPI,
    hasPresale,
    hasPresaleAccess,
    isSaleInMoreThanADay,
    saleEnded,
    isSoldOut,
    isPresale,
    canMintSale,
    isSaleRunning: hasOngoingSale,
  });
};
